import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  RealtimeAudienceEnum,
  RealtimeAudienceParams,
  RealtimeMessageMap,
} from 'realtimer/realtimerAudiences';
import { useDebouncedCallback } from 'use-debounce';
import { RealtimerProviderContext } from '../../realtimer/RealtimerProviderContext';

const realtimerAudiences = {
  [RealtimeAudienceEnum.HubStateUpdated]: ({ expertId }: { expertId: string }) =>
    `ExpertHubStateUpdated-${expertId}`,
  [RealtimeAudienceEnum.NotificationInserted]: ({ expertId }: { expertId: string }) =>
    `ExpertNotificationInserted-${expertId}`,
  [RealtimeAudienceEnum.JobOrderApplicationStatusUpdated]: ({ expertId }: { expertId: string }) =>
    `ExpertJobOrderApplicationStatusUpdated-${expertId}`,
  [RealtimeAudienceEnum.AirPrepping]: ({ expertId }: { expertId: string }) =>
    `ExpertAirPrepping-${expertId}`,
};

export const useRealtimer = <T extends RealtimeAudienceEnum>(
  audience: T,
  params: RealtimeAudienceParams[T],
  callback: (message: RealtimeMessageMap[T]) => any,
  debounceMs = 100,
) => {
  const { subscribe, unsubscribe, isConnected } = useContext(RealtimerProviderContext);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const stableCallback = useCallback(
    (message: RealtimeMessageMap[T]) => callbackRef.current(message),
    [],
  );
  const debouncedCallback = useDebouncedCallback(stableCallback, debounceMs);
  const handler = debounceMs > 0 ? debouncedCallback : stableCallback;

  const audienceKey = realtimerAudiences[audience](params);

  useEffect(() => {
    if (isConnected && isSubscribed) {
      return;
    }

    if (isConnected && !isSubscribed) {
      subscribe(audienceKey, handler);
      setIsSubscribed(true);
    }
  }, [isConnected, isSubscribed, subscribe, setIsSubscribed]);

  useEffect(() => {
    if (!isSubscribed) {
      return;
    }

    if (isSubscribed && !isConnected) {
      unsubscribe(audienceKey, handler);
      setIsSubscribed(false);
    }
  }, [isSubscribed, isConnected, setIsSubscribed, unsubscribe]);

  useEffect(() => {
    return () => {
      unsubscribe(audienceKey, handler);
      setIsSubscribed(false);
    };
  }, [unsubscribe, setIsSubscribed]);
};
